import React, { useState, useEffect } from 'react';
import { 
  Wand2, ImageIcon, Check, Square, CheckSquare, Loader2, 
  Download, X, Palette, RefreshCw, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import axiosInstance from '../api/axios';

const FILTERS = [
  { id: 'original', name: 'Original', description: 'Keine Änderung' },
  { id: 'bright_clean', name: 'Hell & Klar', description: 'Heller, frischer Look' },
  { id: 'warm_glow', name: 'Warmes Licht', description: 'Warme, einladende Töne' },
  { id: 'high_contrast', name: 'Hoher Kontrast', description: 'Knackige Farben' },
  { id: 'soft_light', name: 'Sanftes Licht', description: 'Weiches Licht' },
  { id: 'vivid', name: 'Lebendig', description: 'Satte Farben' },
  { id: 'studio', name: 'Studio', description: 'Professioneller Look' },
  { id: 'natural', name: 'Natürlich', description: 'Echte Farben' },
];

const BatchImageProcessor = ({ 
  isOpen, 
  onClose, 
  products,
  onSaveResults 
}) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('original');
  const [removeBackground, setRemoveBackground] = useState(false);
  const [backgroundType, setBackgroundType] = useState('white');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [step, setStep] = useState('select'); // 'select', 'settings', 'processing', 'results'

  // Flatten all product images
  const allImages = products?.flatMap(product => 
    (product.images || []).map((img, idx) => ({
      url: img,
      productId: product.id,
      productName: product.name,
      imageIndex: idx
    }))
  ) || [];

  useEffect(() => {
    if (isOpen) {
      setSelectedImages([]);
      setSelectedFilter('original');
      setRemoveBackground(false);
      setResults([]);
      setStep('select');
      setProgress(0);
    }
  }, [isOpen]);

  const handleSelectImage = (image) => {
    setSelectedImages(prev => {
      const exists = prev.find(i => i.url === image.url);
      if (exists) {
        return prev.filter(i => i.url !== image.url);
      } else {
        return [...prev, image];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedImages.length === allImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages([...allImages]);
    }
  };

  const handleProcess = async () => {
    if (selectedImages.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Bild aus');
      return;
    }

    setStep('processing');
    setProcessing(true);
    setProgress(0);

    try {
      const response = await axiosInstance.post('/image-editor/batch-process', {
        images: selectedImages.map(img => ({
          image_url: img.url,
          product_id: img.productId,
          image_index: img.imageIndex
        })),
        filter_name: selectedFilter,
        remove_bg: removeBackground,
        background: backgroundType
      });

      setResults(response.data.results || []);
      setProgress(100);
      setStep('results');
      
      const successful = response.data.successful || 0;
      const failed = response.data.failed || 0;
      
      if (failed === 0) {
        toast.success(`${successful} Bilder erfolgreich bearbeitet!`);
      } else {
        toast.warning(`${successful} erfolgreich, ${failed} fehlgeschlagen`);
      }
    } catch (error) {
      console.error('Batch processing error:', error);
      toast.error('Fehler bei der Stapelverarbeitung');
      setStep('settings');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveResults = async () => {
    const successfulResults = results.filter(r => r.success && r.processed_image);
    
    if (successfulResults.length === 0) {
      toast.error('Keine bearbeiteten Bilder zum Speichern');
      return;
    }

    if (onSaveResults) {
      await onSaveResults(successfulResults);
    }
    
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-[#AF9774]" />
            Stapelverarbeitung - Produktbilder bearbeiten
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Select Images */}
          {step === 'select' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Wählen Sie die Bilder aus, die Sie bearbeiten möchten:
                </p>
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  {selectedImages.length === allImages.length ? (
                    <>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Alle abwählen
                    </>
                  ) : (
                    <>
                      <Square className="h-4 w-4 mr-2" />
                      Alle auswählen ({allImages.length})
                    </>
                  )}
                </Button>
              </div>

              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 max-h-[400px] overflow-y-auto p-1">
                {allImages.map((image, idx) => {
                  const isSelected = selectedImages.find(i => i.url === image.url);
                  return (
                    <div
                      key={idx}
                      onClick={() => handleSelectImage(image)}
                      className={`cursor-pointer border-2 rounded overflow-hidden transition-all ${
                        isSelected ? 'border-[#AF9774] ring-2 ring-[#AF9774]/30' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="aspect-square bg-gray-100 relative">
                        <img 
                          src={image.url} 
                          alt={image.productName}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100%" height="100%"/></svg>';
                          }}
                        />
                        {isSelected && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-[#AF9774] rounded-full flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                      <p className="text-xs p-1 truncate">{image.productName}</p>
                    </div>
                  );
                })}
              </div>

              {allImages.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Keine Produktbilder gefunden</p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Settings */}
          {step === 'settings' && (
            <div className="space-y-6">
              <div className="p-3 bg-[#F5EDE4] rounded-lg">
                <p className="text-sm font-medium">{selectedImages.length} Bilder ausgewählt</p>
              </div>

              {/* Filter Selection */}
              <div>
                <h3 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Palette className="h-4 w-4" />
                  Filter auswählen
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {FILTERS.map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => setSelectedFilter(filter.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        selectedFilter === filter.id 
                          ? 'border-[#AF9774] bg-[#F5EDE4]' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className={`font-medium text-sm ${
                        selectedFilter === filter.id ? 'text-[#AF9774]' : 'text-gray-700'
                      }`}>
                        {filter.name}
                      </span>
                      <p className="text-xs text-gray-500">{filter.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Background Removal */}
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#F5EDE4' }}>
                <h3 className="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
                  <Wand2 className="h-4 w-4" />
                  Hintergrund entfernen (KI)
                </h3>
                
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={removeBackground}
                    onChange={(e) => setRemoveBackground(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#AF9774] focus:ring-[#AF9774]"
                  />
                  <span className="text-sm">Hintergrund bei allen Bildern entfernen</span>
                </label>
                
                {removeBackground && (
                  <div className="ml-8 flex gap-2">
                    <button
                      onClick={() => setBackgroundType('white')}
                      className={`px-4 py-2 rounded text-sm ${
                        backgroundType === 'white'
                          ? 'bg-white border-2 border-[#AF9774] text-[#AF9774]'
                          : 'bg-white border border-gray-300'
                      }`}
                    >
                      ⬜ Weiß
                    </button>
                    <button
                      onClick={() => setBackgroundType('transparent')}
                      className={`px-4 py-2 rounded text-sm ${
                        backgroundType === 'transparent'
                          ? 'bg-white border-2 border-[#AF9774] text-[#AF9774]'
                          : 'bg-white border border-gray-300'
                      }`}
                    >
                      🔲 Transparent
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Processing */}
          {step === 'processing' && (
            <div className="py-12 text-center">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#AF9774]" />
              <h3 className="text-lg font-medium mb-2">Bilder werden verarbeitet...</h3>
              <p className="text-sm text-gray-500 mb-4">
                {selectedImages.length} Bilder werden bearbeitet
              </p>
              <Progress value={progress} className="max-w-md mx-auto" />
            </div>
          )}

          {/* Step 4: Results */}
          {step === 'results' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    {results.filter(r => r.success).length} von {results.length} Bildern erfolgreich bearbeitet
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[400px] overflow-y-auto">
                {results.map((result, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    {result.success ? (
                      <>
                        <div className="aspect-square bg-gray-100">
                          <img 
                            src={result.processed_image} 
                            alt="Bearbeitet"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="p-2 bg-green-50">
                          <div className="flex items-center gap-1 text-green-600 text-xs">
                            <Check className="h-3 w-3" />
                            Erfolgreich
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="aspect-square bg-gray-100 flex items-center justify-center">
                          <AlertCircle className="h-8 w-8 text-red-400" />
                        </div>
                        <div className="p-2 bg-red-50">
                          <div className="flex items-center gap-1 text-red-600 text-xs">
                            <X className="h-3 w-3" />
                            Fehler
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-between items-center pt-4 border-t">
          <div>
            {step !== 'select' && step !== 'processing' && (
              <Button 
                variant="outline" 
                onClick={() => setStep(step === 'settings' ? 'select' : 'settings')}
              >
                Zurück
              </Button>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              {step === 'results' ? 'Schließen' : 'Abbrechen'}
            </Button>
            
            {step === 'select' && (
              <Button 
                onClick={() => setStep('settings')}
                disabled={selectedImages.length === 0}
                style={{ backgroundColor: '#AF9774' }}
              >
                Weiter ({selectedImages.length} ausgewählt)
              </Button>
            )}
            
            {step === 'settings' && (
              <Button 
                onClick={handleProcess}
                disabled={processing}
                style={{ backgroundColor: '#AF9774' }}
              >
                <Wand2 className="h-4 w-4 mr-2" />
                {selectedImages.length} Bilder bearbeiten
              </Button>
            )}
            
            {step === 'results' && results.some(r => r.success) && (
              <Button 
                onClick={handleSaveResults}
                style={{ backgroundColor: '#AF9774' }}
              >
                <Download className="h-4 w-4 mr-2" />
                Bearbeitete Bilder übernehmen
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BatchImageProcessor;
